import { ITEM_TYPE, OwnerType, PATCH_STATUSES, PATCH_STATUSES_3DS, TRANSACTION_TYPES } from 'shared/consts/const';
import { Booking, BookingMadeBy, BookingStatus } from 'store/bookings/types';
import { TRANSACTION_STATES } from 'shared/consts';
import { AddOnDto } from '../add-ons/types';
import { VendorAddOnDto } from '../booking-add-ons/types';
import { User } from 'store/user/types';

export interface GetTransactionsAPIResponse {
  data: GetTransactionsResponse;
  /* How many rows match the request's filters across every page: `COUNT(DISTINCT id)` server-side,
   * and the only way to tell a full last page from a full middle one. */
  total: number;
}

export interface GetTransactionsResponse {
  transactions: TransactionDetails[];
}

/* Both fields optional: an empty payload is the first page, which is what the call sites that dispatch
 * `getTransactions.request({})` to mean "refresh" rely on. */
export interface GetTransactionsPageRequest {
  offset?: number;
  limit?: number;
}

/* `offset` is echoed off the request so the reducer decides replace-vs-extend from the offset asked for
 * rather than the current array length. */
export interface GetTransactionsPageSuccess {
  transactions: TransactionDetails[];
  offset: number;
  total: number;
}

export interface TransactionsState {
  transactions: TransactionDetails[];
  /* A field of its own, not a partition of `transactions`: the two come from two reads with different
   * server-side filters, and each tab must keep its rows while the other is still loading. */
  pastTransactions: TransactionDetails[];
  /* The end-of-list signal, a count rather than a flag: a page of exactly `BOOKINGS_PAGE_SIZE` rows is
   * indistinguishable from a full page mid-list, so `transactions.length < total` is the only test. */
  transactionsTotal: number | null;
  /** The same count for the past list. See `transactionsTotal`. */
  pastTransactionsTotal: number | null;
  /* Where the next read starts, counted from the windows the server has answered and not from the rows
   * on screen: the dedupe shortens the list, and a short list re-reads a window already consumed. */
  transactionsNextOffset: number;
  /** The same mark for the past list. See `transactionsNextOffset`. */
  pastTransactionsNextOffset: number;
  transaction: TransactionDetails;
  getTransaction: AsyncActionTypes;
  getTransactions: AsyncActionTypes;
  getPastTransactions: AsyncActionTypes;
  /** Mirrors `loading`, for the past read alone -- the two tabs show their own skeletons. */
  pastLoading: boolean;
  patchTransaction: PatchTransaction;
  loading: boolean;
  processed: boolean;
  refreshing: boolean;
  error?: SerializableError;
  getRedirectTransaction: AsyncActionTypes;
  redirectTransaction: RedirectTransactionDetails;
  checkIn: AsyncActionTypes;
  currentTransaction: TransactionDetails;
}

export interface PatchTransaction extends AsyncActionTypes {
  type: PATCH_STATUSES | PATCH_STATUSES_3DS;
}

export interface TransactionDetails {
  uuid: string;
  id: number;
  transaction_platform: string;
  payment_platform: string;
  total_price: number;
  total_charge: number;
  total_redeemed: number;
  total_discount?: number;
  total_tax: number;
  currency_type: string;
  type: TRANSACTION_TYPES;
  details: TransactionBookingDetails;
  content: ResourceBookingDetails;
  payment_method?: PaymentMethod;
}

export interface CheckInDetails {
  status?: string;
  transactionId?: number;
}

export interface PaymentMethod {
  type?: SupportedPaymentMethods;
  card_type?: string;
  last_four?: string;
  exp_month?: number;
  exp_year?: number;
  remaining_points?: number;
}

export enum SupportedPaymentMethods {
  INVOICE = 'INVOICE',
  CREDIT_CARD = 'CREDIT_CARD',
  APPLE_PAY = 'APPLE_PAY',
  CREDIT_POINTS = 'CREDIT_POINTS',
  AFTER_PAYMENT = 'AFTER_PAYMENT',
  CREDITS = 'CREDITS',
}

export enum AttendeeStatus {
  NONE = 'none',
  ACCEPTED = 'accepted',
  DECLINED = 'declined',
  CANCELLED = 'cancelled',
  TENTATIVE = 'tentative',
  CHECKED_IN = 'checked_in',
}

export enum AttendeeType {
  ORGANIZER = 'organizer',
  TENANT = 'tenant',
  VISITOR = 'visitor',
  GUEST = 'guest',
}

export interface AttendeeRO {
  id: number;
  uuid: string;
  email?: string;
  status?: AttendeeStatus;
  type?: AttendeeType;
  optional?: boolean;
  booking_id?: number;
  booking?: Booking;
  user_uuid?: string;
  user?: User;
  first_name?: string;
  last_name?: string;
  external_id?: string;
  created_at?: Date;
  updated_at?: Date;
  deleted_at?: Date;
}

export interface CloudKey {
  id: number;
  uuid: string;
  label: string;
  url: string;
}

export interface TransactionBookingDetails {
  name: string;
  id: string | number;
  uuid: string;
  allow_cancel_during?: boolean;
  allow_late_cancel?: boolean;
  cancel_during_fee_amount?: number;
  cancel_during_fee_percent?: number;
  late_cancel_fee_amount?: number;
  late_cancel_fee_percent?: number;
  resource: {
    id: string;
    name: string;
    features?: Array<ResourceFeature>;
    amenities_description?: string;
    timezone?: string;
  };
  resource_id: string;
  owner: {
    owner_type: OwnerType;
    owner_uuid: string;
  };
  location_name: string;
  user_id: number;
  start_at: string;
  end_at: string;
  created_at: string;
  /**
   * RES-1473. Shared by every booking one checkout created, and sent only when it created more than
   * one, so a surface holding a single booking can tell it is one day of several.
   */
  checkout_group_uuid?: string | null;
  /** RES-1395. Who made the booking -- see `BookingMadeBy` / `isMadeByPropertyTeam`. */
  made_by?: BookingMadeBy | null;
  status: BookingStatus;
  /**
   * RES-1174. Why the landlord rejected this booking, lifted off the same status row as `status`
   * above, so it always describes the transition being displayed.
   *
   * Always present on the wire, `null` far more often than not: only the native deny route
   * captures a reason, while the Prism mapper also writes `Denied_Available` and captures none.
   * Branch on truthiness rather than on `!== null` -- a helix rollback would still return denied
   * bookings (the `statuses` request parameter predates this work) with the key absent entirely.
   */
  reason?: string | null;
  canceled_at?: string;
  cancellation_description: string;
  cancellation_window_end_minutes: number;
  user_info?: Array<FormResponse>;
  discount_applied: number;
  check_in: CheckInDetails;
  add_ons?: Array<AddOnDto>;
  /* Flagged buildings only; absent otherwise. */
  vendor_add_ons?: Array<VendorAddOnDto>;
  attendees?: Array<AttendeeRO>;
  openpath_cloud_keys?: Array<CloudKey>;
}

export interface ResourceFeature {
  translations?: Record<string, ResourceFeatureTranslationsData>;
  name?: string | null;
  description?: string | null;
  default_type?: DefaultTypeEnum;
  icon?: string | null;
  cta_type?: CTATypeEnum;
  cta_value?: string | null;
  active?: boolean;
  created_at?: Date | null;
  deleted_at?: Date | null;
  updated_at?: Date | null;
  description_uuid?: string | null;
  id?: number | null;
  name_uuid?: string | null;
  resource_id?: number | null;
  uuid?: string | null;
  to_delete?: boolean | null;
}

export enum CTATypeEnum {
  URL = 'URL',
  EMAIL = 'EMAIL',
  PHONE = 'PHONE',
}

export enum DefaultTypeEnum {
  TV = 'TV',
  WIFI = 'WIFI',
  PHONE = 'PHONE',
}

interface ResourceFeatureTranslationsData {
  name: string;
  description: string;
}

export interface GetTransactionResponse {
  data: TransactionDetails;
}

interface ResourceBookingDetails {
  uuid: string;
  start_at: Date;
  end_at: Date;
  status: string;
  resource: {
    name: string;
    description: string;
    image_url: string;
  };
}

export interface PatchTransactionDto {
  transactionId: string;
  status?: PATCH_STATUSES;
  status_3ds?: PATCH_STATUSES_3DS;
  cartId?: string;
}

export interface GetUserTransactionPayload {
  itemType: ITEM_TYPE;
  itemId: string;
}

export interface RedirectTransactionDetails {
  token: string;
  succeeded: boolean;
  message: string;
  transaction_type: string;
  state: TRANSACTION_STATES;
}

export interface RedirectTransactionResponse {
  data: { transaction: RedirectTransactionDetails };
}

export interface UpdateCheckInResponse {
  status: string;
  transactionId?: number;
}

export interface UpdateCheckInAPIResponse {
  data: { check_in: { status: string } };
}
